import { Link, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { t } from '@/shared/lib/i18n'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type WebProfile from '@/shared/types/web/WebProfile'

interface ProfileShowProps {
  profile: WebProfile
  [key: string]: unknown
}

const ProfileShow = () => {
  const { profile } = usePage<ProfileShowProps>().props
  // REBUILD-498: gate the Edit entry point on the self-edit permission (PROFILE/update) so no
  // role that cannot edit its profile is shown an Edit button that opens an unusable form. The
  // permission matrix is the single source of truth — no hardcoded role check (AC5).
  const canEditProfile = useCan(Resources.PROFILE, Actions.UPDATE)

  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || '—'

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('profile.pageTitle')}</h1>
      </header>

      <div className="rounded-lg border p-6 space-y-6">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold">{fullName}</h2>
          {canEditProfile && (
            <Button asChild variant="outline">
              <Link href="/profile/edit">{t('profile.editTitle')}</Link>
            </Button>
          )}
        </div>

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-sm font-medium text-muted-foreground">
              {t('profile.personalInfo.jobTitle')}
            </dt>
            <dd className="text-sm [overflow-wrap:anywhere]">{profile.jobTitle ?? '—'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-sm font-medium text-muted-foreground">
              {t('profile.personalInfo.phone')}
            </dt>
            <dd className="text-sm [overflow-wrap:anywhere]">
              {profile.phone ? [profile.phoneCode, profile.phone].filter(Boolean).join(' ') : '—'}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-sm font-medium text-muted-foreground">
              {t('profile.personalInfo.employeeId')}
            </dt>
            <dd className="text-sm [overflow-wrap:anywhere]">{profile.employeeId ?? '—'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-sm font-medium text-muted-foreground">
              {t('profile.personalInfo.email')}
            </dt>
            <dd className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
              <span className="[overflow-wrap:anywhere]">{profile.email}</span>
              <Badge variant="secondary" className="text-xs">
                {t('profile.personalInfo.emailReadOnly')}
              </Badge>
            </dd>
          </div>
        </dl>
      </div>

      <div className="flex gap-4">
        <Button asChild variant="link" className="px-0">
          <Link href="/notification_preference">{t('profile.notificationPreferencesLink')}</Link>
        </Button>
      </div>
    </div>
  )
}

export default ProfileShow
